/**
 * The reviews tree, loaded from WordPress — `loadEditorialTree()`.
 *
 * WordPress owns which topic belongs to which scope, what a type is called and in which
 * order the types sit (`GET api/editorial-tree`, WP request v6). This module turns that
 * answer into the `EditorialTree` every reader works from — the coverage pages, the
 * sitemap, the header document, the WP hub chips — so the header's count and the page it
 * links can never disagree on what "Earbuds" means: they read one object.
 *
 * Cache-with-live-fallback, the `/api/header-nav` template, in order of preference:
 *   1. fresh slot (≤ FRESH_MS old)                 → instant
 *   2. a rebuild, awaited at most WAIT_MS           → one slow request, then cached
 *   3. the last-good slot, which never expires     → yesterday's tree beats no tree
 *   4. the compiled-in fallback (`taxonomy.ts`), ids resolved slug by slug
 * The rebuild is single-flight and keeps running past the wait to fill the slots; an
 * absent endpoint (not deployed on this install yet: 404) is memoised like any other
 * failure, so it costs one probe per FRESH_MS, not one per page.
 *
 * "Fresh for minutes, not a day" (7067f717): WordPress purges its edge copy on every topic
 * edit, so the site follows an editor within ~one FRESH_MS plus the CDN's own TTL.
 *
 * A WordPress answer is rejected WHOLE (last-good kept) when it is structurally incoherent
 * — see `parseEditorialTree`. A pre-seed tree (seven scopes, every one empty) is the common
 * case: it must not replace a working fallback. Each distinct problem warns once per process.
 */

import { decodeEntities } from 'lib/decode-entities'
import {
  EDITORIAL_HUBS,
  FALLBACK_SCOPES,
  FALLBACK_TREE,
  type EditorialScope,
  type EditorialTree,
  type EditorialType,
  type WpHubRoot,
} from './taxonomy'
import { fetchEditorialTreeRaw, lookupTopicId, warnOnce, type TermLookup } from './wp'

/** How long an accepted WordPress tree is served without asking again. */
const FRESH_MS = 5 * 60 * 1000
/** How long a request waits for a cold rebuild before serving last-good or the fallback. */
const WAIT_MS = 1_500
/** The rebuild itself may take longer — it fills the slots for the next request. */
const FETCH_TIMEOUT_MS = 10_000
/** A degraded fallback resolution (WP did not answer for some slug) is retried sooner. */
const FALLBACK_RETRY_MS = 60 * 1000
/** Slug lookups for the fallback tree run a few at a time: WP drops requests under fan-out. */
const LOOKUP_CONCURRENCY = 4

const SLUG_SHAPE = /^[a-z0-9-]+$/
const HUB_IDS = new Set(EDITORIAL_HUBS.map(h => h.id))

interface Slot {
  tree: EditorialTree
  at: number
}

let fresh: Slot | null = null
let lastGood: EditorialTree | null = null
let attemptedAt = 0
let inFlight: Promise<EditorialTree | null> | null = null

let fallbackSlot: Slot | null = null
let fallbackInFlight: Promise<EditorialTree> | null = null

/**
 * Type slug → scope id for every type any accepted tree has carried in this process. A
 * type slug that vanishes (an editor re-parented or un-scoped the topic) still 301s to its
 * scope page instead of 404ing while WordPress holds no redirect for it.
 */
const rememberedTypes = new Map<string, string>()

export interface LoadOptions {
  /**
   * False for readers that only need the tree's SHAPE (header tabs on every page): they get
   * the static fallback instantly rather than waiting for its slug → id resolution. Readers
   * that query WordPress with the ids (coverage, sitemap, header document) leave it on.
   */
  needIds?: boolean
}

/** The tree to read now. Never throws, never 503s a page: the fallback always exists. */
export async function loadEditorialTree(opts: LoadOptions = {}): Promise<EditorialTree> {
  const now = Date.now()
  if (fresh && now - fresh.at < FRESH_MS) {
    return fresh.tree
  }
  if (!inFlight && now - attemptedAt >= FRESH_MS) {
    attemptedAt = now
    inFlight = rebuild().finally(() => {
      inFlight = null
    })
  }
  if (inFlight) {
    const built = await withDeadline(inFlight, WAIT_MS)
    if (built) {
      return built
    }
  }
  if (lastGood) {
    return lastGood
  }
  if (opts.needIds === false) {
    return fallbackSlot?.tree ?? FALLBACK_TREE
  }
  return resolveFallbackTree()
}

/**
 * Synchronous reader for code that cannot await — the WP page mapper re-pointing hub chips.
 * Serves whatever the process holds (fresh → last-good → resolved fallback → static
 * fallback) and kicks a refresh behind it. Ids may be absent on the static fallback; the
 * mapper matches by slug and never needs them.
 */
export function currentTree(): EditorialTree {
  if (!fresh || Date.now() - fresh.at >= FRESH_MS) {
    void loadEditorialTree({ needIds: false })
  }
  return fresh?.tree ?? lastGood ?? fallbackSlot?.tree ?? FALLBACK_TREE
}

/** The scope a now-vanished type slug last belonged to, or null if never seen. */
export function rememberedTypeScope(typeSlug: string): string | null {
  return rememberedTypes.get(typeSlug) ?? null
}

async function rebuild(): Promise<EditorialTree | null> {
  try {
    const raw = await fetchEditorialTreeRaw(FETCH_TIMEOUT_MS)
    if (raw === undefined) {
      warnOnce(
        'tree:unavailable',
        '[editorial-tree] WordPress did not answer api/editorial-tree — serving last-good or the compiled-in tree'
      )
      return null
    }
    const reference = lastGood ?? fallbackSlot?.tree ?? FALLBACK_TREE
    const { tree, problems } = parseEditorialTree(raw, reference)
    for (const problem of problems) {
      warnOnce(`tree:${problem}`, `[editorial-tree] ${problem}`)
    }
    if (!tree) {
      return null
    }
    fresh = { tree, at: Date.now() }
    lastGood = tree
    remember(tree)
    return tree
  } catch {
    return null
  }
}

function remember(tree: EditorialTree): void {
  for (const scope of tree.scopes) {
    for (const type of scope.types) {
      rememberedTypes.set(type.slug, scope.id)
    }
  }
}

async function withDeadline<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const late = new Promise<null>(resolve => {
    timer = setTimeout(() => resolve(null), ms)
  })
  try {
    return await Promise.race([promise, late])
  } finally {
    clearTimeout(timer)
  }
}

/* ────────────────────────────── the WordPress answer ────────────────────────────── */

/** What `api/editorial-tree` sends, before validation (WP request v6, Ask 3). */
interface RawScope {
  id?: unknown
  label?: unknown
  noun?: unknown
  subject?: unknown
  dealhunt_slug?: unknown
  intro?: unknown
  hubs?: unknown
  order?: unknown
  topic_ids?: unknown
  /** The scope's own top-level topic slugs, for slug-keyed readers. Absent on a WordPress
   *  older than the hub roots: the fallback scope's slugs stand in. */
  topic_slugs?: unknown
  types?: unknown
}

/** One row of `hubs[]`: the hub map (`EditorialHub::HUBS`) and its root page's copy. */
interface RawHub {
  id?: unknown
  label?: unknown
  category?: unknown
  prefix?: unknown
  root_title?: unknown
  root_meta_title?: unknown
  root_intro?: unknown
}

interface RawType {
  id?: unknown
  slug?: unknown
  name?: unknown
  noun?: unknown
  label?: unknown
  intro?: unknown
  order?: unknown
  topic_ids?: unknown
}

export interface ParsedTree {
  /** Null when the answer was rejected whole. */
  tree: EditorialTree | null
  /** One line per distinct problem — rejections and drops alike. */
  problems: string[]
}

/**
 * Validates and merges a WordPress tree. Exported for the spec.
 *
 * Rejected WHOLE (the caller keeps last-good) when:
 *  - the body is not `{ scopes: [...] }` of well-formed scopes and types;
 *  - every scope is empty (the pre-seed state: the registry has rows, no topic is placed);
 *  - a scope has no topics while `reference` (last-good, else the fallback) had some —
 *    an editor cleared a scope, or the answer is partial;
 *  - a type slug equals a scope id, or two scopes carry the same type slug — a path
 *    segment must name one thing (`/reviews/<scope>/<type>/`, `?filter=<type>`).
 * Dropped with a warning: a scope WordPress knows but has no topics for (no page to serve),
 * a type with no term ids.
 *
 * Merge rule: WordPress wins on everything it sends. A WordPress that sends no
 * `topic_slugs` (older than the hub roots) gets a scope's own top-level slugs from the
 * fallback scope of the same id (they matter only to slug-keyed readers: the archive link,
 * the hub chips).
 *
 * `hubs[]` never rejects the tree: it carries copy, not structure. A hub this app does not
 * route is skipped; one whose category, prefix or label differs from `EDITORIAL_HUBS` warns —
 * the two maps describe the same routes and crumbs and must be changed together.
 */
export function parseEditorialTree(raw: unknown, reference: EditorialTree): ParsedTree {
  const problems: string[] = []
  const body = raw as { scopes?: unknown; hubs?: unknown } | null
  if (!body || typeof body !== 'object' || !Array.isArray(body.scopes)) {
    return { tree: null, problems: ['rejected: body is not { scopes: [] }'] }
  }

  const scopes: EditorialScope[] = []
  const scopeIds = new Set<string>()
  const typeSlugs = new Map<string, string>()
  for (const item of body.scopes as unknown[]) {
    const rawScope = (item && typeof item === 'object' ? item : {}) as RawScope
    const id = typeof rawScope.id === 'string' ? rawScope.id : ''
    const label = typeof rawScope.label === 'string' ? decodeEntities(rawScope.label).trim() : ''
    if (!SLUG_SHAPE.test(id) || !label) {
      return { tree: null, problems: [`rejected: malformed scope ${JSON.stringify(rawScope.id)}`] }
    }
    if (scopeIds.has(id)) {
      return { tree: null, problems: [`rejected: scope "${id}" appears twice`] }
    }
    scopeIds.add(id)
    const topicIds = readIds(rawScope.topic_ids)
    if (!topicIds) {
      return { tree: null, problems: [`rejected: scope "${id}" has malformed topic_ids`] }
    }
    const types: EditorialType[] = []
    if (rawScope.types !== undefined && !Array.isArray(rawScope.types)) {
      return { tree: null, problems: [`rejected: scope "${id}" has malformed types`] }
    }
    for (const entry of (rawScope.types ?? []) as unknown[]) {
      const rawType = entry as RawType | null
      const slug = typeof rawType?.slug === 'string' ? rawType.slug : ''
      const name = typeof rawType?.name === 'string' ? decodeEntities(rawType.name).trim() : ''
      const typeTopicIds = readIds(rawType?.topic_ids)
      if (!SLUG_SHAPE.test(slug) || !name || !typeTopicIds) {
        return { tree: null, problems: [`rejected: malformed type in scope "${id}"`] }
      }
      if (typeTopicIds.length === 0) {
        problems.push(`dropped: type "${slug}" has no term ids`)
        continue
      }
      const owner = typeSlugs.get(slug)
      if (owner !== undefined) {
        return {
          tree: null,
          problems: [`rejected: type "${slug}" is in scopes "${owner}" and "${id}"`],
        }
      }
      typeSlugs.set(slug, id)
      types.push({
        slug,
        noun: optionalString(rawType?.noun) ?? name.toLowerCase(),
        label: optionalString(rawType?.label) ?? name,
        intro: optionalString(rawType?.intro),
        topicIds: typeTopicIds,
      })
    }
    const fallback = FALLBACK_SCOPES.find(s => s.id === id) ?? null
    const hubs = readHubs(rawScope.hubs, id, problems)
    scopes.push({
      id,
      label,
      short: optionalString(rawScope.noun) ?? fallback?.short ?? label.toLowerCase(),
      subject: optionalString(rawScope.subject),
      intro: optionalString(rawScope.intro),
      topicSlugs: readSlugs(rawScope.topic_slugs) ?? (fallback ? [...fallback.topicSlugs] : []),
      topicIds,
      types,
      dealHuntSlug: optionalString(rawScope.dealhunt_slug),
      hubs,
    })
  }

  for (const scope of scopes) {
    for (const type of scope.types) {
      if (scopeIds.has(type.slug)) {
        return {
          tree: null,
          problems: [`rejected: type "${type.slug}" shares its slug with a scope id`],
        }
      }
    }
  }

  if (scopes.every(s => s.topicIds.length === 0)) {
    return {
      tree: null,
      problems: ['rejected: no scope has a topic (registry seeded, no topic placed)'],
    }
  }

  const kept: EditorialScope[] = []
  for (const scope of scopes) {
    if (scope.topicIds.length > 0) {
      kept.push(scope)
      continue
    }
    const before = reference.scopes.find(s => s.id === scope.id)
    if (before && scopeHadTopics(before)) {
      return {
        tree: null,
        problems: [`rejected: scope "${scope.id}" lost every topic (had ${describe(before)})`],
      }
    }
    problems.push(`dropped: scope "${scope.id}" has no topic yet`)
  }

  return {
    tree: {
      scopes: kept,
      hubRoots: readHubRoots(body.hubs, problems),
      source: 'wp',
      degraded: false,
    },
    problems,
  }
}

/** WordPress's SEO title carries the site name; the pages add their own (and a page number). */
const SITE_SUFFIX = /\s+[-–—|]+\s+SoundGuys$/

function readHubRoots(value: unknown, problems: string[]): Record<string, WpHubRoot> {
  const roots: Record<string, WpHubRoot> = {}
  if (!Array.isArray(value)) {
    return roots
  }
  for (const entry of value as unknown[]) {
    const raw = (entry && typeof entry === 'object' ? entry : {}) as RawHub
    const hub = EDITORIAL_HUBS.find(h => h.id === raw.id)
    if (!hub) {
      continue
    }
    if (
      raw.category !== hub.categorySlug ||
      `/${String(raw.prefix)}` !== hub.path ||
      optionalString(raw.label) !== hub.label
    ) {
      problems.push(`hub "${hub.id}": WordPress maps it to another category, prefix or label`)
    }
    roots[hub.id] = {
      title: optionalString(raw.root_title),
      metaTitle: optionalString(raw.root_meta_title)?.replace(SITE_SUFFIX, '') ?? null,
      intro: optionalString(raw.root_intro),
    }
  }
  return roots
}

function scopeHadTopics(scope: EditorialScope): boolean {
  return scope.topicIds.length > 0 || scope.topicSlugs.length > 0 || scope.types.length > 0
}

function describe(scope: EditorialScope): string {
  return scope.topicIds.length > 0
    ? `${scope.topicIds.length} term ids`
    : `${scope.topicSlugs.length + scope.types.length} slugs`
}

function readIds(value: unknown): number[] | null {
  if (value === undefined || value === null) {
    return []
  }
  if (!Array.isArray(value)) {
    return null
  }
  const ids: number[] = []
  for (const id of value as unknown[]) {
    if (typeof id !== 'number' || !Number.isInteger(id) || id <= 0) {
      return null
    }
    if (!ids.includes(id)) {
      ids.push(id)
    }
  }
  return ids
}

function readSlugs(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null
  }
  return (value as unknown[]).filter(
    (s): s is string => typeof s === 'string' && SLUG_SHAPE.test(s)
  )
}

function readHubs(value: unknown, scopeId: string, problems: string[]): readonly string[] | null {
  if (!Array.isArray(value)) {
    return null
  }
  const hubs: string[] = []
  for (const hub of value as unknown[]) {
    if (typeof hub === 'string' && HUB_IDS.has(hub)) {
      hubs.push(hub)
    } else {
      // An unknown hub is a WP-side option this app has no route for: ignore, don't 404.
      problems.push(`ignored: scope "${scopeId}" names unknown hub ${JSON.stringify(hub)}`)
    }
  }
  return hubs.length > 0 ? hubs : null
}

function optionalString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }
  const text = decodeEntities(value).trim()
  return text === '' ? null : text
}

/* ────────────────────────────── the compiled-in fallback ────────────────────────────── */

/**
 * The fallback tree with term ids resolved slug by slug (`/wp/v2/topic?slug=`, memoised a
 * day per slug in `wp.ts`). A slug WordPress does not know on this install drops out —
 * its type page 404s, honestly; a slug WordPress could not be asked about stays with no
 * ids and marks the tree `degraded`, so its type page answers 503 and is retried soon.
 */
export async function resolveFallbackTree(): Promise<EditorialTree> {
  const now = Date.now()
  if (fallbackSlot) {
    const ttl = fallbackSlot.tree.degraded ? FALLBACK_RETRY_MS : FRESH_MS
    if (now - fallbackSlot.at < ttl) {
      return fallbackSlot.tree
    }
  }
  fallbackInFlight ??= resolveFallback()
    .then(tree => {
      fallbackSlot = { tree, at: Date.now() }
      remember(tree)
      return tree
    })
    .catch((): EditorialTree => fallbackSlot?.tree ?? FALLBACK_TREE)
    .finally(() => {
      fallbackInFlight = null
    })
  return fallbackInFlight
}

async function resolveFallback(): Promise<EditorialTree> {
  const slugs = Array.from(
    new Set(FALLBACK_SCOPES.flatMap(s => [...s.topicSlugs, ...(s.types ?? []).map(t => t.slug)]))
  )
  const lookups = new Map<string, TermLookup>()
  for (let i = 0; i < slugs.length; i += LOOKUP_CONCURRENCY) {
    const batch = slugs.slice(i, i + LOOKUP_CONCURRENCY)
    const results = await Promise.all(batch.map(slug => lookupTopicId(slug)))
    batch.forEach((slug, j) => lookups.set(slug, results[j]))
  }

  let degraded = false
  const scopes: EditorialScope[] = []
  for (const fallback of FALLBACK_SCOPES) {
    const topicSlugs: string[] = []
    const topicIds: number[] = []
    for (const slug of fallback.topicSlugs) {
      const id = lookups.get(slug)
      if (id === null) {
        warnOnce(
          `fallback:missing:${slug}`,
          `[editorial-tree] fallback topic "${slug}" is not on this WordPress`
        )
        continue
      }
      topicSlugs.push(slug)
      if (typeof id === 'number') {
        topicIds.push(id)
      } else {
        degraded = true
      }
    }
    const types: EditorialType[] = []
    for (const type of fallback.types ?? []) {
      const id = lookups.get(type.slug)
      if (id === null) {
        warnOnce(
          `fallback:missing:${type.slug}`,
          `[editorial-tree] fallback type "${type.slug}" is not on this WordPress`
        )
        continue
      }
      const ids = typeof id === 'number' ? [id] : []
      if (ids.length === 0) {
        degraded = true
      }
      types.push({ ...type, intro: null, topicIds: ids })
      topicIds.push(...ids)
    }
    if (topicSlugs.length === 0 && types.length === 0) {
      // Nothing of this scope exists on this install: no page to serve, drop it.
      continue
    }
    scopes.push({
      id: fallback.id,
      label: fallback.label,
      short: fallback.short,
      subject: fallback.subject ?? null,
      intro: null,
      topicSlugs,
      topicIds,
      types,
      dealHuntSlug: fallback.dealHuntSlug ?? null,
      hubs: fallback.hubs ?? null,
    })
  }
  return { scopes, hubRoots: {}, source: 'fallback', degraded }
}

/** Specs only: age the fresh slot out (last-good kept), as FRESH_MS passing would. */
export function expireFreshForTests(): void {
  fresh = null
  attemptedAt = 0
}

/** Specs only: every slot back to cold. */
export function resetEditorialTreeForTests(): void {
  fresh = null
  lastGood = null
  attemptedAt = 0
  inFlight = null
  fallbackSlot = null
  fallbackInFlight = null
  rememberedTypes.clear()
}
